<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Cookies from 'js-cookie';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import { COOKIE_KEY_LANGUAGE } from '@/home/utils/constants';
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuRoot,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  // NavigationMenuLink,
} from 'reka-ui';
import MenuProject from './MenuProject.vue';
import MenuEvents from './MenuEvents.vue';
import MenuJournalism from './MenuJournalism.vue';
import MenuInformation from './MenuInformation.vue';
import MenuCooperation from './MenuCooperation.vue';
import MenuAbout from './MenuAbout.vue';

const { availableLocales, locale } = useI18n();

const isMobile = ref(false);

const languages = computed(() => availableLocales.slice().sort((a) => {
  return a === locale.value ? -1 : 1;
}));

const changeLanguage = (lang: string) => {
  Cookies.set(COOKIE_KEY_LANGUAGE, lang, {
    expires: 3650,
    path: '/',
    sameSite: 'Lax',
  });
  window.location.href = '/';
}

onMounted(() => {
  const userAgent = navigator.userAgent;
  isMobile.value = /iphone|ipad|ipod|android|OpenHarmony/i.test(userAgent);
});
</script>

<template>
  <header
    class="page-header relative h-72 bg-[rgba(255,255,255,0.6)] text-#2d2d2d shadow-[inset_0_-1px_0_0_#e6e7ec] transition-all duration-200 hover:bg-white hover:shadow-[inset_0_-1px_0_0_#e6e7ec,0_0_10px_0_rgba(0,0,0,0.06)]"
  >
    <div class="mx-auto h-full flex-y-center" :class="$route.path === '/' ? 'max-w-1200' : 'max-w-1360 <2xl:max-w-1200'">
      <div class="h-40 w-189">
        <RouterLink to="/" class="block h-full w-full">
          <img src="@/home/assets/common/logo-with-title.svg" alt="LOGO" class="block h-full w-full object-contain">
        </RouterLink>
      </div>

      <NavigationMenuRoot :disable-click-trigger="!isMobile" :delay-duration="100" class="ml-25 h-full flex-grow text-16 first:*:h-full">
        <NavigationMenuList class="m-0 h-full flex list-none p-0">
          <NavigationMenuItem>
            <NavigationMenuTrigger class="h-full cursor-pointer border-none bg-transparent px-20 text-16 hover:text-#1180ff">
              {{ $t('header.nav2') }}
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent>
                <MenuProject />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <NavigationMenuTrigger class="h-full cursor-pointer border-none bg-transparent px-20 text-16 hover:text-#1180ff">
              {{ $t('header.nav5.title') }}
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent>
                <MenuEvents />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem v-if="locale === 'zh-CN'">
            <NavigationMenuTrigger class="h-full cursor-pointer border-none bg-transparent px-20 text-16 hover:text-#1180ff">
              新闻
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent>
                <MenuJournalism />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem v-if="locale === 'zh-CN'">
            <NavigationMenuTrigger class="h-full cursor-pointer border-none bg-transparent px-20 text-16 hover:text-#1180ff">
              信息公开
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent>
                <MenuInformation />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <NavigationMenuTrigger class="h-full cursor-pointer border-none bg-transparent px-20 text-16 hover:text-#1180ff">
              {{ $t('header.nav3') }}
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent>
                <MenuCooperation />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <NavigationMenuTrigger class="h-full cursor-pointer border-none bg-transparent px-20 text-16 hover:text-#1180ff">
              {{ $t('header.nav4') }}
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent>
                <MenuAbout />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <!-- <NavigationMenuItem>
            <NavigationMenuLink
              class="h-full flex-center px-20 text-16 text-#2d2d2d no-underline hover:text-#1180ff"
              href="https://openatom.cn/conference/2025-1/index/main/"
              target="_blank"
            >
              2025生态大会
            </NavigationMenuLink>
          </NavigationMenuItem> -->
        </NavigationMenuList>

        <div class="absolute left-0 right-0 top-full z-70 w-full perspective-[2000px] bg-white">
          <NavigationMenuViewport
            class="mx-auto h-[var(--reka-navigation-menu-viewport-height)] max-w-1200 transition-[width,height] duration-300 data-[state=open]:animate-keyframes-fade-in data-[state=open]:animate-duration-500 data-[state=open]:animate-ease"
          />
        </div>
      </NavigationMenuRoot>

      <div class="flex items-center text-14">
        <Menu as="div" class="relative" v-slot="{ open }">
          <MenuButton class="flex cursor-pointer items-center border-0 bg-transparent p-0 text-brand-normal hover:text-brand-primary" :class="{ 'text-brand-primary': open }">
            <div class="i-home-icons:world mr-8 size-16"></div>
            {{ $t('languageName', '', { locale }) }}
            <div class="i-home-icons:arrow-down ml-8 size-16 transition-transform" :class="{ 'rotate-180': open }"></div>
          </MenuButton>
          <MenuItems class="absolute left-1/2 z-60 mt-32 w-112 rounded-4 bg-white p-16 -translate-x-1\/2">
            <MenuItem v-slot="{ active }" v-for="lang in languages" :key="lang">
              <a
                class="block text-center leading-24 no-underline siblings:mt-16 hover:text-#1180ff visited:text-#2d2d2d"
                :class="{
                  'text-brand-primary': active || (locale === lang),
                  'text-brand-normal': !(active || (locale === lang)),
                  'font-medium': locale === lang
                }"
                href="javascript:;"
                @click="changeLanguage(lang)"
              >
                {{ $t('languageName', '', { locale: lang }) }}
              </a>
            </MenuItem>
          </MenuItems>
        </Menu>
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
</style>
